import { z } from "zod";
import type {
  OrderAlertChannel,
  AwarenessTier,
  AlertTargetKind,
  AlertAcknowledgement,
  CancelAlertTargetKind,
} from "@order-up/models";

/**
 * Zod schema for OrderAlertRoutingConfig — golden-paired with the model at
 * packages/models/src/locations/OrderAlertRouting.ts. The enum literals are // scoped-path-ok: module path
 * re-declared here (zod needs literal tuples) but each carries a compile-time
 * parity guard against the model type, so a drift between the model and this
 * schema fails to compile. The Dart mirror gets a runtime parity test when it
 * lands.
 */

export const AwarenessTierSchema = z.enum(["cloud", "lan", "direct"]);
export const AlertTargetKindSchema = z.enum([
  "none",
  "allDevices",
  "specificDevices",
  "employeeRole",
]);
export const AlertAcknowledgementSchema = z.enum(["informational", "requireAck"]);
export const OrderAlertChannelSchema = z.enum([
  "online",
  "ubereats",
  "doordash",
  "grubhub",
  "chownow",
  "foodhub",
  "gloriafood",
  "flipdish",
  "beyondmenu",
  "cloudwaitress",
  "eatstreet",
  "menufy",
]);

// ── Compile-time parity guards (schema values === model union types) ──────────
type AssertEqual<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never;
const _tierParity: AssertEqual<z.infer<typeof AwarenessTierSchema>, AwarenessTier> = true;
const _targetKindParity: AssertEqual<
  z.infer<typeof AlertTargetKindSchema>,
  AlertTargetKind
> = true;
const _ackParity: AssertEqual<
  z.infer<typeof AlertAcknowledgementSchema>,
  AlertAcknowledgement
> = true;
const _channelParity: AssertEqual<
  z.infer<typeof OrderAlertChannelSchema>,
  OrderAlertChannel
> = true;
void _tierParity;
void _targetKindParity;
void _ackParity;
void _channelParity;

export const AlertTargetSchema = z
  .object({
    kind: AlertTargetKindSchema.readonly(),
    deviceGuids: z.array(z.string().min(1)).optional().readonly(),
    employeeRole: z.string().min(1).max(100).optional().readonly(),
  })
  .strict();

export const AlertEscalationConfigSchema = z
  .object({
    enabled: z.boolean().readonly(),
    tier1Seconds: z.number().int().min(0).optional().readonly(),
    tier2Seconds: z.number().int().min(0).optional().readonly(),
    tier3Seconds: z.number().int().min(0).optional().readonly(),
    autoPauseOnNoAck: z.boolean().optional().readonly(),
    autoPauseSeconds: z.number().int().min(0).optional().readonly(),
  })
  .strict();

/**
 * An alert's sound volume, 0–100. Out-of-range or fractional values are
 * REJECTED, not clamped: the page only produces whole numbers in range, so
 * anything else is a bug upstream that a silent clamp would hide. Readers clamp
 * defensively on their side.
 */
export const AlertVolumePercentSchema = z.number().int().min(0).max(100);

export const ChannelAlertPolicySchema = z
  .object({
    target: AlertTargetSchema.readonly(),
    acknowledgement: AlertAcknowledgementSchema.readonly(),
    tiers: z.array(AwarenessTierSchema).readonly(),
    escalation: AlertEscalationConfigSchema.optional().readonly(),
    soundEnabled: z.boolean().optional().readonly(),
    volumePercent: AlertVolumePercentSchema.optional().readonly(),
  })
  .strict();

/**
 * The cancel takeover's target. Same shape as {@link AlertTargetSchema}, but
 * `employeeRole` is refused HERE (and only here — new-order routing still takes
 * it): the takeover shows on the PIN screen, where nobody is signed in.
 */
const CancelAlertTargetKindSchema = z.enum(["none", "allDevices", "specificDevices"]);
const _cancelTargetKindParity: AssertEqual<
  z.infer<typeof CancelAlertTargetKindSchema>,
  CancelAlertTargetKind
> = true;
void _cancelTargetKindParity;

export const CancelAlertTargetSchema = AlertTargetSchema.refine(
  (target) => CancelAlertTargetKindSchema.safeParse(target.kind).success,
  { message: "Cancel alerts go to all tills, specific tills, or none — not a job title" },
);

export const CancelAlertConfigSchema = z
  .object({
    target: CancelAlertTargetSchema.optional().readonly(),
    soundEnabled: z.boolean().optional().readonly(),
    volumePercent: AlertVolumePercentSchema.optional().readonly(),
  })
  .strict();

export const OrderAlertRoutingConfigSchema = z
  .object({
    enabled: z.boolean().readonly(),
    defaultPolicy: ChannelAlertPolicySchema.optional().readonly(),
    // Per-channel map keyed by OrderAlertChannel; PARTIAL by design (a channel
    // without an entry falls back to defaultPolicy). z.record with an enum key
    // is exhaustive in zod v4, so use partialRecord to allow a subset of keys
    // while still rejecting unknown channel keys.
    channels: z
      .partialRecord(OrderAlertChannelSchema, ChannelAlertPolicySchema)
      .readonly(),
    cancelAlert: CancelAlertConfigSchema.optional().readonly(),
  })
  .strict();

export type AlertTargetInput = z.infer<typeof AlertTargetSchema>;
export type ChannelAlertPolicyInput = z.infer<typeof ChannelAlertPolicySchema>;
export type CancelAlertConfigInput = z.infer<typeof CancelAlertConfigSchema>;
export type OrderAlertRoutingConfigInput = z.infer<typeof OrderAlertRoutingConfigSchema>;
